import { inject, Injectable } from '@angular/core';

import {
  ConversationItem,
  ConversationTypeEnum,
  FileItem,
  MessageAttachmentItem,
  MessageContentTypeEnum,
  MessageItem,
  MessageTypeEnum,
  PublicUnseenMetrics
} from '@appTypes/chat-api.types';
import { fileDownload } from '@core/misc/misc.utils';
import { DataLoadStatusModel, RequestStateModel } from '@core/store/store.models';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { append, insertItem, patch, removeItem, updateItem, ɵPatchSpec } from '@ngxs/store/operators';
import { DynamicModalService } from '@shared/modules/dynamic-modal/dynamic-modal.service';
import { uniqueId } from 'lodash-es';
import { catchError, forkJoin, of, tap, throwError } from 'rxjs';
import { CONVERSATIONS_DEFAULT_LIMIT, MESSAGES_DEFAULT_LIMIT } from '../chat.const';
import { ChatService } from '../services/chat.service';
import {
  chatConversationMarkAsSeen,
  chatCreateConversation,
  chatCreateDraftConversation,
  chatCreateMessage,
  chatDownloadAttachment,
  chatGetUnseenMessagesCount,
  chatInit,
  chatLoadChatBox,
  chatLoadChatBoxes,
  chatLoadConversationsList,
  chatLoadMessagesList,
  chatOpenConversation,
  chatOpenConversationsList,
  chatReset,
  chatResetConversationsList,
  chatSendMessage,
  chatSendMessageByReference,
  chatUpdateConversation,
  chatUploadMessageAttachments,
  chatUpsertMessages
} from './chat.actions';
import { ChatBoxModel, ChatMessageModel, ChatStateModel, ConversationsListModel } from './chat.model';
import {
  setLastMessageOnConversation,
  updateConversation,
  upsertChatBox,
  upsertChatBoxMessage
} from './chat.state-utils';

const defaults = new ChatStateModel();

@State<ChatStateModel>({
  name: 'chat',
  defaults
})
@Injectable()
export class ChatState {
  readonly #dynamicModalService = inject(DynamicModalService);
  readonly #chatService = inject(ChatService);

  @Action(chatInit)
  chatInit({ dispatch, patchState }: StateContext<ChatStateModel>, { socketId, user }: chatInit) {
    patchState({ socketId, user });
    dispatch([new chatGetUnseenMessagesCount({ initialLoad: true })]);
  }
  @Action(chatReset)
  chatReset({ setState }: StateContext<ChatStateModel>) {
    setState(defaults);
  }

  @Action(chatGetUnseenMessagesCount)
  chatGetUnseenMessagesCount(
    { setState, dispatch, patchState }: StateContext<ChatStateModel>,
    { payload }: chatGetUnseenMessagesCount
  ) {
    setState(
      patch<ChatStateModel>({
        unseenMessagesCount: patch<RequestStateModel<PublicUnseenMetrics>>({
          loadState: new DataLoadStatusModel('loading')
        })
      })
    );
    return this.#chatService.api.unseenMessagesCount().pipe(
      tap((response) => {
        patchState({
          unseenMessagesCount: new RequestStateModel(response)
        });
        if (response.count && payload?.initialLoad) {
          dispatch(new chatLoadChatBoxes({ unseen: true }));
        }
      }),
      catchError((e) => {
        patchState({
          unseenMessagesCount: new RequestStateModel<PublicUnseenMetrics>(
            new DataLoadStatusModel('error', e?.error?.message ?? 'Failed to load unseen messages count')
          )
        });
        return throwError(() => e);
      })
    );
  }

  @Action(chatOpenConversationsList)
  async chatOpenConversationsList({ dispatch }: StateContext<ChatStateModel>) {
    dispatch(new chatLoadConversationsList());
    // Loaded on first open: keeps the chat UI (Quill editor included) out of the shell chunk.
    const { ConversationsListComponent } = await import(
      '../components/conversations-list/conversations-list.component'
    );
    this.#dynamicModalService.open({
      title: 'All Messages',
      component: ConversationsListComponent,
      styleClass: 'st:w-[600px]! st:h-[600px]!'
    });
  }

  @Action(chatResetConversationsList)
  chatResetConversationsList({ patchState }: StateContext<ChatStateModel>) {
    patchState({
      conversationsList: new ConversationsListModel()
    });
  }

  @Action(chatOpenConversation)
  async chatOpenConversation({ dispatch }: StateContext<ChatStateModel>, { payload }: chatOpenConversation) {
    dispatch(new chatLoadChatBox(payload));
    // Loaded on first open: keeps the chat UI (Quill editor included) out of the shell chunk.
    const { ChatBoxComponent } = await import('../components/chat-box/chat-box.component');
    this.#dynamicModalService.open({
      title: payload.title,
      component: ChatBoxComponent,
      styleClass: 'st:w-[600px]!',
      height: '600px',
      data: { chatBoxId: payload.reference + '|' + payload.referenceId, ...payload }
    });
  }

  @Action(chatLoadChatBox)
  chatLoadChatBox({ getState, setState, dispatch }: StateContext<ChatStateModel>, { payload }: chatLoadChatBox) {
    const chatBoxId = payload.reference + '|' + payload.referenceId;
    const currentChatBox = getState()?.chatBoxes?.find((e) => e.id === chatBoxId);

    if (currentChatBox?.conversationId && !currentChatBox?.conversation?.unreadMetrics?.count) return;

    setState(
      patch<ChatStateModel>({
        chatBoxes: insertItem({
          loadState: new DataLoadStatusModel('loading'),
          id: chatBoxId,
          messagesLoadState: new DataLoadStatusModel('loading'),
          messagesisInitialLoad: true
        })
      })
    );
    return this.#chatService.api.loadConversation(payload).pipe(
      tap((response) => {
        if (response === null) {
          dispatch(new chatCreateDraftConversation(payload));
          return;
        }
        const chatBox: ChatBoxModel = {
          id: chatBoxId,
          conversationId: response.conversation._id,
          conversation: response.conversation,
          messages:
            response.messages
              ?.map((e) => ({
                item: e,
                messageId: e._id,
                ts: e.senderTimestamp,
                loadState: new DataLoadStatusModel('completed')
              }))
              ?.slice()
              ?.sort((a, b) =>
                a.item?.createdAt?.getTime && b?.item?.createdAt?.getTime
                  ? a.item.createdAt.getTime() - b.item.createdAt.getTime()
                  : a.ts - b.ts
              ) ?? [],
          messagesCanLoadMore: response.messages.length >= MESSAGES_DEFAULT_LIMIT,
          messagesisInitialLoad: true,
          messagesLoadState: new DataLoadStatusModel('completed'),
          loadState: new DataLoadStatusModel('completed')
        };
        setState(
          patch<ChatStateModel>({
            chatBoxes: updateItem<ChatBoxModel>(
              (e) => e.id === chatBoxId,
              patch<ChatBoxModel>({
                ...chatBox
              })
            )
          })
        );
      }),
      catchError((e) => {
        setState(
          patch<ChatStateModel>({
            chatBoxes: updateItem<ChatBoxModel>(
              (e) => e.id === chatBoxId,
              patch<ChatBoxModel>({
                loadState: new DataLoadStatusModel('error', e.message)
              })
            )
          })
        );
        return throwError(() => e);
      })
    );
  }

  @Action(chatLoadChatBoxes)
  chatLoadChatBoxes({ setState }: StateContext<ChatStateModel>, { payload }: chatLoadChatBoxes) {
    return this.#chatService.api.conversationList(payload).pipe(
      tap((response) => {
        response?.items.forEach((conversation: ConversationItem) => {
          const chatBox: ChatBoxModel = {
            id: conversation.reference + '|' + conversation.referenceId,
            conversationId: conversation._id,
            conversation: conversation,
            messagesLoadState: new DataLoadStatusModel(),
            loadState: new DataLoadStatusModel('completed'),
            messagesisInitialLoad: true
          };
          setState(upsertChatBox(chatBox));
        });
      })
    );
  }
  @Action(chatConversationMarkAsSeen)
  chatConversationMarkAsSeen(
    { setState, dispatch, getState }: StateContext<ChatStateModel>,
    { payload: { conversationId } }: chatConversationMarkAsSeen
  ) {
    const chatBox = getState().chatBoxes.find((c) => c.conversationId === conversationId);
    if (!chatBox?.conversation?.unreadMetrics?.count) return;
    setState(
      patch<ChatStateModel>({
        conversationsList: patch<ConversationsListModel>({
          items: updateItem<ConversationItem>(
            (c) => c._id === chatBox.conversationId,
            patch<ConversationItem>({
              hasUnseenMessages: false,
              unreadMetrics: {
                count: 0
              }
            })
          )
        }),
        chatBoxes: updateItem<ChatBoxModel>(
          (c) => c.id === chatBox.id,
          patch<ChatBoxModel>({
            conversation: patch<ConversationItem>({
              _id: conversationId,
              unreadMetrics: { count: 0 },
              hasUnseenMessages: false
            })
          })
        )
      })
    );
    return this.#chatService.wsActions.conversationMarkAsSeen(conversationId).pipe(
      tap(() => {
        setTimeout(() => {
          dispatch(new chatGetUnseenMessagesCount());
        }, 1000);
      })
    );
  }
  @Action(chatLoadConversationsList)
  chatLoadConversationsList({ getState, setState }: StateContext<ChatStateModel>) {
    const {
      conversationsList: { loadState, hasMore, nextCursor }
    } = getState();
    if (loadState.status === 'loading') return;
    if (hasMore === false) return;

    setState(
      patch<ChatStateModel>({
        conversationsList: patch<ConversationsListModel>({
          loadState: new DataLoadStatusModel('loading')
        })
      })
    );
    return this.#chatService.api
      .conversationList({
        cursor: nextCursor,
        limit: CONVERSATIONS_DEFAULT_LIMIT
      })
      .pipe(
        tap((response) => {
          setState(
            patch<ChatStateModel>({
              conversationsList: patch<ConversationsListModel>({
                loadState: new DataLoadStatusModel('completed'),
                items: append(response.items),
                isInitialLoad: nextCursor ? false : true,
                hasMore: response.hasMore,
                nextCursor: response.nextCursor
              })
            })
          );
        }),
        catchError((e) => {
          setState(
            patch<ChatStateModel>({
              conversationsList: patch<ConversationsListModel>({
                loadState: new DataLoadStatusModel('error', e.message)
              })
            })
          );
          return throwError(() => e);
        })
      );
  }

  @Action(chatLoadMessagesList)
  chatLoadMessagesList(
    { getState, setState }: StateContext<ChatStateModel>,
    { payload: { conversationId } }: chatLoadMessagesList
  ) {
    const chatBox = getState().chatBoxes.find((c) => c.conversationId === conversationId);
    if (!chatBox) return;
    const { messagesLoadState: loadState, messagesCanLoadMore } = chatBox;

    if (loadState.status === 'loading') return;

    if (messagesCanLoadMore === false) return;

    const updateChatBoxState = (data: NoInfer<ɵPatchSpec<ChatBoxModel>>) => {
      setState(
        patch<ChatStateModel>({
          chatBoxes: updateItem<ChatBoxModel>((c) => c.id === chatBox.id, patch<ChatBoxModel>({ ...data }))
        })
      );
    };
    updateChatBoxState({
      messagesLoadState: new DataLoadStatusModel('loading')
    });
    return this.#chatService.api
      .messages(conversationId, {
        skip: chatBox.messages?.length ?? 0,
        limit: MESSAGES_DEFAULT_LIMIT
      })
      .pipe(
        tap((response) => {
          try {
            let messages: ChatMessageModel[] = [...(chatBox.messages ?? [])];
            response?.forEach((item: MessageItem) => {
              if (messages.some((m) => m.messageId === item._id)) return;
              messages.push({
                item,
                loadState: new DataLoadStatusModel('completed'),
                messageId: item._id,
                ts: item.senderTimestamp ?? item.createdAt?.getTime()
              });
            });
            messages = messages
              .slice()
              .sort((a, b) =>
                a.item?.createdAt?.getTime && b?.item?.createdAt?.getTime
                  ? a.item.createdAt.getTime() - b.item.createdAt.getTime()
                  : a.ts! - b.ts!
              );

            updateChatBoxState({
              messagesLoadState: new DataLoadStatusModel('completed'),
              messages,
              messagesCanLoadMore: response.length >= MESSAGES_DEFAULT_LIMIT,
              messagesisInitialLoad: false
            });
          } catch (error) {
            console.error('Error processing messages:', error);
          }
        }),
        catchError((e) => {
          updateChatBoxState({
            messagesLoadState: new DataLoadStatusModel('error', e.message)
          });
          return throwError(() => e);
        })
      );
  }

  @Action(chatCreateDraftConversation)
  chatCreateDraftConversation(
    { getState, setState }: StateContext<ChatStateModel>,
    { payload }: chatCreateDraftConversation
  ) {
    const chatBoxId = payload.reference + '|' + payload.referenceId;
    const currentUser = getState().user;
    const conversationTempId = uniqueId('tempId_');
    setState(
      patch<ChatStateModel>({
        chatBoxes: updateItem<ChatBoxModel>(
          (e) => e.id === chatBoxId,
          patch<ChatBoxModel>({
            id: chatBoxId,
            conversationId: conversationTempId,
            conversation: {
              _id: conversationTempId,
              attributes: payload.attributes,
              createdAt: new Date(),
              updatedAt: new Date(),
              type: ConversationTypeEnum.PublicGroup,
              participants: [currentUser!],
              rooms: [],
              status: 'open',
              reference: payload.reference,
              referenceId: payload.referenceId,
              assignedUser: currentUser!,
              __v: 0
            },
            messages: [],
            messagesLoadState: new DataLoadStatusModel('completed'),
            messagesCanLoadMore: false,
            messagesisInitialLoad: true,
            isDraft: true,
            loadState: new DataLoadStatusModel('completed')
          })
        )
      })
    );
  }

  @Action(chatCreateConversation)
  chatCreateConversation(
    { setState, getState, dispatch }: StateContext<ChatStateModel>,
    { draftChatboxId, draftMessageId }: chatCreateConversation
  ) {
    const chatBox = getState().chatBoxes.find((c) => c.id === draftChatboxId);
    if (!chatBox) throw new Error('Chat box not found');
    if (!chatBox?.conversation || !chatBox.conversation?.reference || !chatBox.conversation?.referenceId)
      throw new Error('Conversation data is missing');

    if (draftMessageId)
      setState(
        patch<ChatStateModel>({
          chatBoxes: updateItem<ChatBoxModel>(
            (e) => e.id === draftChatboxId,
            patch<ChatBoxModel>({
              messages: updateItem<ChatMessageModel>(
                (m) => m.item?._id === draftMessageId,
                patch<ChatMessageModel>({
                  loadState: new DataLoadStatusModel('loading')
                })
              )
            })
          )
        })
      );
    return this.#chatService.wsActions
      .conversationCreate({
        reference: chatBox.conversation.reference,
        referenceId: chatBox.conversation.referenceId,
        attributes: chatBox.conversation.attributes
      })
      .pipe(
        tap((response) => {
          setState(
            patch<ChatStateModel>({
              chatBoxes: updateItem<ChatBoxModel>(
                (e) => e.id === draftChatboxId,
                patch<ChatBoxModel>({
                  id: draftChatboxId,
                  conversationId: response._id,
                  conversation: response,
                  ...(draftMessageId
                    ? {
                        messages: updateItem<ChatMessageModel>(
                          (m) => m.item?._id === draftMessageId,
                          patch<ChatMessageModel>({
                            item: patch<MessageItem>({
                              conversationId: response._id
                            }),
                            loadState: new DataLoadStatusModel('completed')
                          })
                        )
                      }
                    : { messages: [] }),
                  isDraft: false,
                  loadState: new DataLoadStatusModel('completed')
                })
              )
            })
          );
          if (draftMessageId) dispatch(new chatSendMessage(response._id!, draftMessageId));
        })
      );
  }

  @Action(chatCreateMessage)
  chatCreateMessage(
    { getState, setState, dispatch }: StateContext<ChatStateModel>,
    { conversationId, payload, attachmentsToUpload }: chatCreateMessage
  ) {
    const userData = getState().user;
    if (!userData) {
      return;
    }
    const chatBox = getState().chatBoxes.find((c) => c.conversationId === conversationId);

    if (!chatBox) return;

    const timeStamp = new Date().getTime();

    const temporaryId = uniqueId('tempId_') + timeStamp.toString();

    const messageItem: MessageItem = {
      _id: temporaryId,
      content: payload?.content ?? '',
      senderTimestamp: timeStamp,
      sender: userData,
      contentType: MessageContentTypeEnum.Text,
      conversationId,
      createdAt: new Date(),
      messageType: MessageTypeEnum.PublicIncoming,
      updatedAt: new Date(),
      attachments: attachmentsToUpload
        ? attachmentsToUpload.map((e) => ({
            _id: e.tmpId,
            contentType: e.file.type,
            name: e.file.name,
            createdAt: new Date(),
            size: e.file.size
          }))
        : []
    };
    setState(
      patch<ChatStateModel>({
        chatBoxes: updateItem<ChatBoxModel>(
          (c) => c.conversationId === conversationId,
          patch<ChatBoxModel>({
            conversation: patch<ConversationItem>({
              lastMessage: messageItem
            }),
            messages: insertItem(
              {
                item: messageItem,
                loadState: new DataLoadStatusModel(),
                ts: timeStamp,
                messageId: temporaryId,
                attachmentsToUpload: attachmentsToUpload
              },
              chatBox.messages?.length ?? 0
            )
          })
        ),
        conversationsList: patch<ConversationsListModel>({
          items: updateItem<ConversationItem>(
            (c) => c._id === conversationId,
            patch<ConversationItem>({
              lastMessage: messageItem
            })
          )
        })
      })
    );

    return dispatch(new chatSendMessage(conversationId, temporaryId));
  }
  @Action(chatSendMessage)
  chatSendMessage(
    { getState, setState, dispatch }: StateContext<ChatStateModel>,
    { conversationId, messageId }: chatSendMessage
  ) {
    const userData = getState().user;
    if (!userData) {
      return;
    }
    const chatBox = getState().chatBoxes.find((c) => c.conversationId === conversationId);

    if (!chatBox) return;

    const messageData = chatBox?.messages?.find((m) => m?.messageId === messageId);
    if (!messageData?.item) return;

    // If draft conversation, create it first
    if (chatBox.isDraft) return dispatch(new chatCreateConversation(chatBox.id!, messageId));

    // If there are attachments to upload, upload them first
    if (messageData.attachmentsToUpload?.length)
      return dispatch(new chatUploadMessageAttachments({ conversationId, messageId }));

    const updateMessageState = (message: Partial<ChatMessageModel>, messageId?: string) => {
      setState(
        patch<ChatStateModel>({
          chatBoxes: updateItem<ChatBoxModel>(
            (c) => c.conversationId === conversationId,
            patch<ChatBoxModel>({
              conversation: patch<ConversationItem>({
                lastMessage: message.item
              }),
              messages: updateItem((m) => m?.messageId === (messageId ?? message.messageId), patch(message))
            })
          )
        })
      );
    };

    const timeStamp = new Date().getTime();

    updateMessageState({
      item: messageData.item,
      loadState: new DataLoadStatusModel('loading'),
      ts: timeStamp,
      messageId
    });
    return this.#chatService.wsActions
      .messageSend(conversationId, {
        content: messageData.item.content ?? '',
        senderTimestamp: messageData.item.senderTimestamp,
        attachments: messageData.item.attachments
      })
      .pipe(
        tap((response) => {
          updateMessageState(
            {
              ...messageData.item,
              item: response,
              loadState: new DataLoadStatusModel('completed'),
              ts: response.senderTimestamp,
              messageId: response._id
            },
            messageId
          );
        }),
        catchError((e) => {
          updateMessageState(
            {
              ...messageData.item,
              loadState: new DataLoadStatusModel('error', e?.message ?? 'Failed to send message')
            },
            messageId
          );
          return throwError(() => e);
        })
      );
  }

  @Action(chatSendMessageByReference)
  chatSendMessageByReference(
    { dispatch, setState }: StateContext<ChatStateModel>,
    { payload }: chatSendMessageByReference
  ) {
    return this.#chatService.wsActions.sendMessageByReference(payload).pipe(
      tap((response) => {
        dispatch(new chatUpsertMessages([response]));
        setState(setLastMessageOnConversation(response));
      }),
      catchError((e) => {
        console.error('Failed to send message by reference', e);
        return throwError(() => e);
      })
    );
  }
  @Action(chatDownloadAttachment)
  chatDownloadAttachment({ getState, setState }: StateContext<ChatStateModel>, { payload }: chatDownloadAttachment) {
    const chatBox = getState().chatBoxes.find((c) => c.conversationId === payload.conversationId);
    if (!chatBox?.id) return;
    setState(
      patch<ChatStateModel>({
        chatBoxes: updateItem<ChatBoxModel>(
          (e) => e.id === chatBox.id,
          patch<ChatBoxModel>({
            messages: updateItem<ChatMessageModel>(
              (m) => m.messageId === payload.messageId,
              patch<ChatMessageModel>({
                attachmentsLoadState: new DataLoadStatusModel('loading', { id: payload.attachment._id })
              })
            )
          })
        )
      })
    );
    return this.#chatService.api.downloadAttachment(payload.conversationId, payload.attachment._id).pipe(
      tap((response) => {
        fileDownload(response, payload.attachment.name);
        setState(
          patch<ChatStateModel>({
            chatBoxes: updateItem<ChatBoxModel>(
              (e) => e.id === chatBox.id,
              patch<ChatBoxModel>({
                messages: updateItem<ChatMessageModel>(
                  (m) => m.messageId === payload.messageId,
                  patch<ChatMessageModel>({
                    attachmentsLoadState: new DataLoadStatusModel('completed')
                  })
                )
              })
            )
          })
        );
      }),
      catchError((e) => {
        setState(
          patch<ChatStateModel>({
            chatBoxes: updateItem<ChatBoxModel>(
              (e) => e.id === chatBox.id,
              patch<ChatBoxModel>({
                messages: updateItem<ChatMessageModel>(
                  (m) => m.messageId === payload.messageId,
                  patch<ChatMessageModel>({
                    attachmentsLoadState: new DataLoadStatusModel(
                      'error',
                      e?.error?.message ?? 'Failed to download attachment'
                    ),
                    loadState: new DataLoadStatusModel('error', e?.error?.message ?? 'Failed to download attachment')
                  })
                )
              })
            )
          })
        );
        return throwError(() => e);
      })
    );
  }
  @Action(chatUpsertMessages)
  chatUpsertMessages({ getState, setState, dispatch }: StateContext<ChatStateModel>, { payload }: chatUpsertMessages) {
    const conversationIdsToLoad: string[] = [];
    payload.forEach((message) => {
      const chatBox = getState().chatBoxes.find((c) => c.conversationId === message.conversationId);
      if (!chatBox && message.conversationId) {
        conversationIdsToLoad.push(message.conversationId);
        return;
      }

      setState(setLastMessageOnConversation(message));
      setState(upsertChatBoxMessage(message, chatBox?.messages?.length ?? 0));
    });
    if (conversationIdsToLoad.length) {
      dispatch(new chatLoadChatBoxes({ ids: conversationIdsToLoad }));
    }
  }

  @Action(chatUpdateConversation)
  chatUpdateConversation(
    { getState, setState, dispatch }: StateContext<ChatStateModel>,
    { payload }: chatUpdateConversation
  ) {
    const conversationIdsToLoad: string[] = [];
    payload.forEach((conversation) => {
      const chatBox = getState().chatBoxes.find((c) => c.conversationId === conversation._id);
      const existingConversation = getState().conversationsList.items.find((c) => c._id === conversation._id);
      if (!chatBox && conversation._id) {
        conversationIdsToLoad.push(conversation._id);
        if (!existingConversation) return;
      }
      setState(updateConversation(conversation));
    });

    if (conversationIdsToLoad.length) {
      dispatch(new chatLoadChatBoxes({ ids: conversationIdsToLoad }));
    }
  }

  @Action(chatUploadMessageAttachments)
  chatUploadMessageAttachments(
    { getState, setState, dispatch }: StateContext<ChatStateModel>,
    { payload }: chatUploadMessageAttachments
  ) {
    const chatBox = getState().chatBoxes.find((c) => c.conversationId === payload.conversationId);
    if (!chatBox?.id) return;
    const attachmentsToUpload = chatBox.messages?.find((m) => m.messageId === payload.messageId)?.attachmentsToUpload;
    if (!attachmentsToUpload?.length) return;

    const updateState = (loadState: DataLoadStatusModel<{ id: string }>, attachmentId: string, file?: FileItem) => {
      setState(
        patch<ChatStateModel>({
          chatBoxes: updateItem<ChatBoxModel>(
            (e) => e.id === chatBox.id,
            patch<ChatBoxModel>({
              messages: updateItem<ChatMessageModel>(
                (m) => m.messageId === payload.messageId,
                patch<ChatMessageModel>({
                  attachmentsLoadState: loadState,
                  ...(file?._id
                    ? {
                        attachmentsToUpload: removeItem((f) => f.tmpId === attachmentId),
                        item: patch<MessageItem>({
                          attachments: updateItem<MessageAttachmentItem>(
                            (e) => e._id === attachmentId,
                            patch<MessageAttachmentItem>({
                              _id: file._id,
                              name: file.name,
                              size: file.size,
                              contentType: file.contentType,
                              createdAt: file.createdAt
                            })
                          )
                        })
                      }
                    : {})
                })
              )
            })
          )
        })
      );
    };
    const uploadAttachment$ = attachmentsToUpload.map((file) =>
      this.#chatService.api.uploadAttachment(payload.conversationId, file.file).pipe(
        tap((response) => {
          updateState(new DataLoadStatusModel('completed', { id: file.tmpId }), file.tmpId, response);
        }),
        catchError((e) => {
          updateState(new DataLoadStatusModel('error', { id: file.tmpId }), file.tmpId);
          return of(e);
        })
      )
    );

    return forkJoin(uploadAttachment$).pipe(
      tap(() => {
        setState(
          patch<ChatStateModel>({
            chatBoxes: updateItem<ChatBoxModel>(
              (e) => e.id === chatBox.id,
              patch<ChatBoxModel>({
                messages: updateItem<ChatMessageModel>(
                  (m) => m.messageId === payload.messageId,
                  patch<ChatMessageModel>({
                    attachmentsLoadState: new DataLoadStatusModel('completed')
                  })
                )
              })
            )
          })
        );
        dispatch(new chatSendMessage(payload.conversationId, payload.messageId));
      })
    );
  }

  /* Selectors */

  @Selector([ChatState])
  static unseenMessagesCount({ unseenMessagesCount }: ChatStateModel) {
    return unseenMessagesCount.response?.count;
  }

  static unseenMessagesCountByReference(payload: Pick<ConversationItem, 'reference' | 'referenceId'>) {
    return createSelector([ChatState], ({ chatBoxes }: ChatStateModel): number => {
      return (
        chatBoxes?.find(
          (c) => c.conversation?.reference === payload.reference && c.conversation?.referenceId === payload.referenceId
        )?.conversation?.unreadMetrics?.count ?? 0
      );
    });
  }
  static chatBox(chatBoxId: string) {
    return createSelector([ChatState], ({ chatBoxes }: ChatStateModel) => {
      return chatBoxes.find((c) => c.id === chatBoxId);
    });
  }

  static messages(conversationId: string) {
    return createSelector([ChatState], ({ chatBoxes }: ChatStateModel) => {
      return chatBoxes.find((c) => c.conversationId === conversationId);
    });
  }

  @Selector([ChatState])
  static userData({ user }: ChatStateModel) {
    return user;
  }

  @Selector([ChatState])
  static userId({ user }: ChatStateModel) {
    return user?.id;
  }
  @Selector([ChatState])
  static loginName({ user }: ChatStateModel) {
    return user?.loginName;
  }

  @Selector([ChatState])
  static conversationsList({ conversationsList }: ChatStateModel) {
    return conversationsList;
  }
  @Selector([ChatState])
  static socketId({ socketId }: ChatStateModel) {
    return socketId;
  }
}
